/**
 * SupportWidget — root component: launcher + panel, replacing the interim
 * SupportContactPopover (#497). Owns the store-driven view switch (Home /
 * Messages / Help tabs, Compose, Thread) and stands down while the mobile
 * nav drawer is open, mirroring the popover it replaces.
 *
 * Help articles are NOT a panel view — HelpTab/HomeTab navigate straight to
 * the full-page reading experience at /help/:articleId and close the widget,
 * since long content, images, and video embeds don't fit the 384px panel.
 */

import { useEffect, useRef } from 'react';
import styled from 'styled-components';
import { formatSupportReference } from '@upskill/shared';
import { media } from '../../../styles/media';
import { useUIStore } from '../../../stores/uiStore';
import { useAuthStore } from '../../../stores/authStore';
import { useSupportWidgetStore } from '../../../stores/supportWidgetStore';
import { useSupportUnreadCount } from '../../../hooks/useSupport';
import { SupportLauncher, SUPPORT_LAUNCHER_SIZE_PX } from './SupportLauncher';
import { SupportPanel } from './SupportPanel';
import { HomeTab } from './tabs/HomeTab';
import { MessagesTab } from './tabs/MessagesTab';
import { HelpTab } from './tabs/HelpTab';
import { ComposeView } from './ComposeView';
import { ThreadView } from './ThreadView';

export { SUPPORT_LAUNCHER_SIZE_PX };

const Wrapper = styled.div`
  position: fixed;
  right: ${({ theme }) => theme.spacing[6]};
  bottom: ${({ theme }) => theme.spacing[6]};
  z-index: ${({ theme }) => theme.zIndex.fixed};

  ${media.tablet} {
    right: ${({ theme }) => theme.spacing[4]};
    bottom: ${({ theme }) => theme.spacing[4]};
  }
`;

export function SupportWidget() {
  const isMobileSidebarOpen = useUIStore((s) => s.isMobileSidebarOpen);
  const user = useAuthStore((s) => s.user);
  const { data: unreadCount = 0 } = useSupportUnreadCount();

  const isOpen = useSupportWidgetStore((s) => s.isOpen);
  const view = useSupportWidgetStore((s) => s.view);
  const threadId = useSupportWidgetStore((s) => s.threadId);
  const threadHeader = useSupportWidgetStore((s) => s.threadHeader);
  const toggleWidget = useSupportWidgetStore((s) => s.toggleWidget);
  const closeWidget = useSupportWidgetStore((s) => s.closeWidget);
  const goHome = useSupportWidgetStore((s) => s.goHome);
  const setActiveTab = useSupportWidgetStore((s) => s.setActiveTab);
  const openThread = useSupportWidgetStore((s) => s.openThread);

  const launcherRef = useRef<HTMLButtonElement>(null);
  const wasOpenRef = useRef(isOpen);

  // Return focus to the launcher when the panel closes (Escape, close button,
  // toggling the bubble) — SupportPanel takes focus on open but never gives
  // it back, so without this it lands on <body> for keyboard/SR users.
  useEffect(() => {
    if (wasOpenRef.current && !isOpen) {
      launcherRef.current?.focus();
    }
    wasOpenRef.current = isOpen;
  }, [isOpen]);

  // Requester notification emails/bell deep-link to `/?support=<id>`
  // (supportRequesterLinkUrl) expecting the widget to open straight to that
  // thread — there's no page mounted at that route. NotificationBell already
  // handles the in-app click case; this covers a cold page load (email link,
  // browser reload) by reading the query param once on mount and stripping
  // it so a later refresh doesn't reopen the same thread.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const supportRequestId = params.get('support');
    if (!supportRequestId) return;

    openThread(supportRequestId);

    params.delete('support');
    const nextSearch = params.toString();
    const nextUrl =
      window.location.pathname + (nextSearch ? `?${nextSearch}` : '') + window.location.hash;
    window.history.replaceState(null, '', nextUrl);
    // Mount-only: this consumes the URL's initial query param, not a live subscription to it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Mirrors SupportContactPopover: the mobile nav drawer shares this z-index
  // tier, so stand down entirely while it's open rather than paint over it.
  if (isMobileSidebarOpen) return null;

  let headerTitle: string | undefined;
  let headerSubtitle: string | undefined;
  let onBack: (() => void) | undefined;

  if (view === 'compose') {
    headerTitle = 'New message';
    onBack = goHome;
  } else if (view === 'thread') {
    // The requester is the one person who never sees their own subject line
    // if this just says "Conversation" (ADO #542 items 9/10) — falls back to
    // that neutral copy only while ThreadView's detail query hasn't resolved.
    headerTitle = threadHeader?.subject ?? 'Conversation';
    headerSubtitle = threadHeader ? formatSupportReference(threadHeader.reference) : undefined;
    onBack = () => setActiveTab('messages');
  } else if (view === 'help') {
    headerTitle = 'Knowledge Center';
  } else if (view === 'messages') {
    headerTitle = '';
  }

  return (
    <Wrapper>
      {isOpen && (
        <SupportPanel
          userFirstName={user?.firstName}
          onClose={closeWidget}
          headerTitle={headerTitle}
          headerSubtitle={headerSubtitle}
          onBack={onBack}
        >
          {view === 'home' && <HomeTab />}
          {view === 'messages' && <MessagesTab />}
          {view === 'help' && <HelpTab />}
          {view === 'compose' && <ComposeView onSubmitted={(id) => openThread(id)} />}
          {view === 'thread' && threadId && <ThreadView requestId={threadId} />}
        </SupportPanel>
      )}
      <SupportLauncher ref={launcherRef} isOpen={isOpen} unreadCount={unreadCount} onClick={toggleWidget} />
    </Wrapper>
  );
}

export default SupportWidget;
